/**
 * F3.01 — Query key factories for the System Management module (Users, Roles,
 * Role Actions). Mirrors the shape of `election-keys.ts` so consumers see a
 * familiar API and `queryClient.invalidateQueries({ queryKey: <module>.all })`
 * wipes everything for that resource at once.
 *
 * Key design notes:
 *  - Lists are keyed by a stable serialization of the filter object so two
 *    queries with the same filters share a cache entry.
 *  - "Actions for role" lookups live under `roleKeys` because they describe a
 *    role's surface, not the action catalogue.
 *  - The dashboard / aggregator queries live under `<resource>.dashboard` so
 *    a single `invalidate(...dashboard())` resets them without nuking lists.
 */

export const roleKeys = {
	all: ['roles'] as const,
	lists: () => [...roleKeys.all, 'list'] as const,
	list: (filters: Record<string, unknown>) => [...roleKeys.lists(), filters] as const,
	details: () => [...roleKeys.all, 'detail'] as const,
	detail: (id: string | number) => [...roleKeys.details(), id] as const,
	byName: (name: string) => [...roleKeys.all, 'byName', name] as const,
	usersInRole: (roleName: string) => [...roleKeys.all, 'usersInRole', roleName] as const,
	actionsForRole: (roleName: string) => [...roleKeys.all, 'actionsForRole', roleName] as const,
	// F3.10 — used to cache the "actions per role" map driven by the dashboard
	// waterfall. The set of roles is the cache key so the map is regenerated
	// only when the role set actually changes.
	actionCounts: (roleNames: string[]) => [...roleKeys.all, 'actionCounts', [...roleNames].sort()] as const,
};

export const roleActionKeys = {
	all: ['roleActions'] as const,
	lists: () => [...roleActionKeys.all, 'list'] as const,
	list: (filters: Record<string, unknown>) => [...roleActionKeys.lists(), filters] as const,
	catalogue: () => [...roleActionKeys.all, 'catalogue'] as const,
	details: () => [...roleActionKeys.all, 'detail'] as const,
	detail: (id: string | number) => [...roleActionKeys.details(), id] as const,
	rolesForAction: (actionId: string | number) => [...roleActionKeys.all, 'rolesForAction', actionId] as const,
	unassignedForRole: (roleId: string | number) => [...roleActionKeys.all, 'unassignedForRole', roleId] as const,
};

export const userKeys = {
	all: ['users'] as const,
	lists: () => [...userKeys.all, 'list'] as const,
	list: (filters: Record<string, unknown>) => [...userKeys.lists(), filters] as const,
	details: () => [...userKeys.all, 'detail'] as const,
	detail: (id: string | number) => [...userKeys.details(), id] as const,
	byEmail: (email: string) => [...userKeys.all, 'byEmail', email] as const,
	byPhone: (phone: string) => [...userKeys.all, 'byPhone', phone] as const,
	rolesOfUser: (email: string) => [...userKeys.all, 'rolesOfUser', email] as const,
	admins: () => [...userKeys.all, 'admins'] as const,
	// Keyed on the party because the scope comes from the `PartyId` REQUEST HEADER, not the
	// URL — two parties share one URL, so without this they would share one cache entry.
	candidates: (partyId: string) => [...userKeys.all, 'candidates', partyId] as const,
};

export const apiKeyKeys = {
	all: ['apiKeys'] as const,
	lists: () => [...apiKeyKeys.all, 'list'] as const,
	list: (filters: Record<string, unknown>) => [...apiKeyKeys.lists(), filters] as const,
	details: () => [...apiKeyKeys.all, 'detail'] as const,
	detail: (id: string | number) => [...apiKeyKeys.details(), id] as const,
};

/**
 * Service Registry (api.bot). `stats`, `channel` and `numbers` sit beside `lists()` rather than under
 * it, so invalidating the list after a create does not also refetch every open Numbers page.
 */
export const serviceRegistryKeys = {
	all: ['serviceRegistry'] as const,
	lists: () => [...serviceRegistryKeys.all, 'list'] as const,
	details: () => [...serviceRegistryKeys.all, 'detail'] as const,
	detail: (id: string) => [...serviceRegistryKeys.details(), id] as const,
	stats: () => [...serviceRegistryKeys.all, 'stats'] as const,
	channel: () => [...serviceRegistryKeys.all, 'channel'] as const,
	numbersFor: (id: string) => [...serviceRegistryKeys.all, 'numbers', id] as const,
	numbers: (id: string, filters: Record<string, unknown>) => [...serviceRegistryKeys.numbersFor(id), filters] as const,
};
